/**
 * useLookupInputRanges — shared state machine the slash-command-enabled
 * textareas (`AgenticSearchInput`, `AgenticLeftPanel`) use to track confirmed
 * entity ranges inside their textarea value.
 *
 * Background: when the user picks a suggestion from the slash dropdown we
 * replace the typed `/query` with just the *entity name* (no leading slash) so
 * the input box never shows the `/` glyph. To keep the highlight pill, atomic
 * Backspace, and submit-time wrapping correct we need an explicit positional
 * record of "this stretch of characters is a confirmed entity". This hook
 * owns that list and keeps it consistent across:
 *
 *   • Free typing/edit (`onEdit`) — ranges before the edit stay put; ranges
 *     after the edit shift by the length delta; ranges that overlap the edit
 *     are dropped because the user has typed inside an entity. Atomic delete
 *     (Backspace over an entity) is just an edit that wipes the whole range.
 *   • Pick from dropdown (`commitPick`) — first rebase existing ranges
 *     against the slash-replace edit, then append the newly-inserted entity
 *     range.
 *   • Find at caret (`findRangeAtCaret`) — locate the range whose right edge
 *     sits at `caretPos`, used by the Backspace handler.
 *   • External clear (e.g. host resets value to '' on submit) — drop all
 *     ranges via the `useEffect` below.
 *
 * Ranges are also projected to a deduplicated `LookupItem[]` and emitted via
 * `onLookupsChange` so the host can keep its mirror in sync.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { LookupItem } from '../AskAsperAgentic.types';
import {
    LookupInputRange,
    rangesToLookups,
    rebaseRangesAfterEdit,
    findRangeEndingAtCaret,
} from './lookupInputTokens';

export interface UseLookupInputRangesOptions {
    /** Current textarea value, used to detect external resets (host clearing). */
    inputValue: string;
    /** Snapshot of host-mirrored selected items; used only to skip no-op emits. */
    selectedLookups?: LookupItem[];
    onLookupsChange?: (items: LookupItem[]) => void;
    /** Called when a confirmed entity range is dropped (Backspace, edit inside token, etc.). */
    onLookupRemoved?: (item: LookupItem) => void;
}

export interface UseLookupInputRangesResult {
    ranges: LookupInputRange[];
    /** Rebase ranges in response to a free edit from `oldValue` -> `newValue`. */
    onEdit: (oldValue: string, newValue: string) => void;
    /** Commit a pick: rebase against `oldValue -> newValue` then append `newRange`. */
    commitPick: (oldValue: string, newValue: string, newRange: LookupInputRange) => void;
    /** Return the range whose right edge sits at `caretPos`, or null. */
    findRangeAtCaret: (caretPos: number) => LookupInputRange | null;
}

function lookupKey(item: LookupItem): string {
    return `${item.column_name}::${item.entity_name}`;
}

function findRemovedLookups(before: LookupInputRange[], after: LookupInputRange[]): LookupItem[] {
    if (before.length === 0) return [];
    const afterKeys = new Set(after.map((r) => lookupKey(r.lookup)));
    const removed: LookupItem[] = [];
    const seen = new Set<string>();
    for (const range of before) {
        const key = lookupKey(range.lookup);
        if (afterKeys.has(key) || seen.has(key)) continue;
        seen.add(key);
        removed.push(range.lookup);
    }
    return removed;
}

function lookupsAreEqual(a: LookupItem[], b: LookupItem[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i].column_name !== b[i].column_name || a[i].entity_name !== b[i].entity_name) return false;
    }
    return true;
}

export function useLookupInputRanges({
    inputValue,
    selectedLookups,
    onLookupsChange,
    onLookupRemoved,
}: UseLookupInputRangesOptions): UseLookupInputRangesResult {
    const [ranges, setRanges] = useState<LookupInputRange[]>([]);
    // `selectedLookups` is unstable across renders — capture in a ref so the
    // `emit` callback below stays referentially stable.
    const selectedLookupsRef = useRef(selectedLookups);
    selectedLookupsRef.current = selectedLookups;
    const onLookupRemovedRef = useRef(onLookupRemoved);
    onLookupRemovedRef.current = onLookupRemoved;
    // Latest-ranges ref so `findRangeAtCaret` returns the freshest list even
    // when called from a stale-closure keydown handler.
    const rangesRef = useRef(ranges);
    rangesRef.current = ranges;

    const emitIfChanged = useCallback((next: LookupInputRange[]) => {
        if (!onLookupsChange) return;
        const items = rangesToLookups(next);
        const current = selectedLookupsRef.current ?? [];
        if (lookupsAreEqual(current, items)) return;
        onLookupsChange(items);
    }, [onLookupsChange]);

    const notifyRemoved = useCallback((before: LookupInputRange[], after: LookupInputRange[]) => {
        const removed = findRemovedLookups(before, after);
        if (removed.length === 0) return;
        removed.forEach((item) => onLookupRemovedRef.current?.(item));
    }, []);

    const onEdit = useCallback(
        (oldValue: string, newValue: string) => {
            if (oldValue === newValue) return;
            setRanges((current) => {
                const rebased = rebaseRangesAfterEdit(oldValue, newValue, current);
                if (rebased !== current) {
                    notifyRemoved(current, rebased);
                    emitIfChanged(rebased);
                }
                return rebased;
            });
        },
        [emitIfChanged, notifyRemoved],
    );

    const commitPick = useCallback(
        (oldValue: string, newValue: string, newRange: LookupInputRange) => {
            setRanges((current) => {
                const rebased = rebaseRangesAfterEdit(oldValue, newValue, current);
                const next = [...rebased, newRange];
                emitIfChanged(next);
                return next;
            });
        },
        [emitIfChanged],
    );

    const findRangeAtCaret = useCallback(
        (caretPos: number) => findRangeEndingAtCaret(caretPos, rangesRef.current),
        [],
    );

    // External-reset bridge: when the host clears the input (e.g. after submit)
    // the controlled `inputValue` switches to ''. Any internal ranges become
    // nonsense, so drop them and notify the host.
    useEffect(() => {
        if (inputValue === '' && ranges.length > 0) {
            setRanges([]);
            emitIfChanged([]);
        }
    }, [inputValue, ranges.length, emitIfChanged]);

    return { ranges, onEdit, commitPick, findRangeAtCaret };
}
